import axios, { AxiosError, AxiosRequestConfig, Canceler } from "axios";
import {
  QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueries,
  useQueryClient,
} from "react-query";
import $RefParser, { JSONSchema } from "@apidevtools/json-schema-ref-parser";

import {
  IMosaic,
  IMosaicRenderOption,
  ITileJSON,
  ITileSettings,
  IQueryableDefinition,
} from "pages/Explore/types";
import { ICqlExpressionList } from "pages/Explore/utils/cql/types";
import { makeFilterBody } from "pages/Explore/utils/hooks/useStacFilter";
import { collectionFilter } from "pages/Explore/utils/stac";
import {
  IIngestionDefinition,
  IIngestionRun,
  ICredentialInfo,
  IStacCollection,
  IStacItem,
  IStacSearchResult,
  ICredentialType,
  IConnectionInfo,
  IManagedIdentity,
  IIngestionDefinitions,
  IIngestionRuns,
} from "types/stac";
import { getTileJsonAsset, makeRasterTileJsonUrl } from "utils";
import {
  DATA_URL,
  IMAGE_URL,
  STAC_COLLECTION_SCHEMA_URI,
  STAC_URL,
  API_VERSION,
  INMA_URL,
} from "./constants";
import { defaultQueryableNames } from "./queryables";
import { AnimationConfig } from "pages/Explore/components/Sidebar/exporters/AnimationExporter/types";
import { ImageConfig } from "pages/Explore/components/Sidebar/exporters/ImageExporter/types";
import { ImageExportResponse } from "pages/Explore/components/Sidebar/exporters/BaseExporter/types";
import { LayerType } from "pages/Explore/enums";
import { isAuthRequest, isGraphRequest } from "components/auth/helpers";
import { useEffect, useState } from "react";
import { updateTileSettings } from "components/management/common/hooks";

export const QUERY_KEYS = {
  ingestions: "ingestions",
  credentials: "credentials",
  credentialsDetail: "credentialsDetail",
  credentialTypes: "credential-types",
  collections: "stac-collections",
  items: "collection-items",
  runs: "ingestion-runs",
  staticFiles: "static-files",
  apiPathFragment: "api-path-fragment",
  tileJson: "tile-json",
  animationExport: "animation-export",
  imageExport: "image-export",
  imageBlob: "image-blob",
  stacCollectionSchema: "stac-collection-schema",
  configManifest: "collection-config",
  mosaics: "mosaics",
  renderOptions: "render-options",
  tileSettings: "tile-settings",
  queryables: "queryables",
};

// Query content can be prefetched if it's likely to be used
export const usePrefetchContent = () => {};

export const useCollection = (id: string | undefined) => {
  return useQuery([QUERY_KEYS.collections, id], getCollection, {
    enabled: Boolean(id),
  });
};

export const useCredentialsWithInfo = (currentPage: number, pageSize: number) => {
  const queryClient = useQueryClient();
  const { data: credentialsResult, isLoading: credentialsLoading } = useQuery(
    [QUERY_KEYS.credentials, currentPage, pageSize],
    getCredentials,
    { enabled: true }
  );

  const credentialSecretsQueries = useQueries(
    (credentialsResult || []).map(credential => ({
      queryKey: [QUERY_KEYS.credentialsDetail, credential.id],
      queryFn: () =>
        getCredentialDetail({
          queryKey: [QUERY_KEYS.credentialsDetail, credential.id],
          meta: undefined,
        }).then(detail => {
          // Cache the runs data
          queryClient.setQueryData(
            [QUERY_KEYS.credentialsDetail, credential.id],
            detail
          );
          return detail;
        }),
      enabled: Boolean(credential.id),
    }))
  );

  const credentialsWithInfo = (credentialsResult || []).map((credential, index) => ({
    ...credential,
    connectionInfo: credentialSecretsQueries[index]?.data || ({} as IConnectionInfo),
  }));

  return {
    data: credentialsWithInfo,
    isLoading: credentialsLoading || credentialSecretsQueries.some(q => q.isLoading),
  };
};

export const useIngestionsWithRuns = (
  id: string | undefined,
  currentPage: number,
  pageSize: number
) => {
  const queryClient = useQueryClient();

  const { data: ingestionsResult, isSuccess: ingestionSuccess } = useQuery(
    [QUERY_KEYS.ingestions, id, currentPage, pageSize],
    getIngestions,
    { enabled: true }
  );

  const [ingestionsWithRuns, setIngestionsWithRuns] = useState<
    IIngestionDefinition[]
  >({} as IIngestionDefinition[]);

  useEffect(() => {
    if (ingestionSuccess && ingestionsResult && ingestionsResult.value) {
      const ingestionRunsQueries = ingestionsResult.value.map(ingestion => ({
        queryFn: () =>
          getIngestionRuns({
            queryKey: [QUERY_KEYS.runs, id, ingestion.id],
            meta: undefined,
          }).then(runs => {
            // Cache the runs data
            queryClient.setQueryData([QUERY_KEYS.runs, id, ingestion.id], runs);
            return runs.value;
          }),
      }));

      const fetchIngestionWithRuns = async () => {
        const results = await Promise.all(
          ingestionRunsQueries.map(
            query => query.queryFn() as Promise<IIngestionRun[]>
          )
        );

        const combinedData = ingestionsResult.value.map((ingestion, index) => ({
          ...ingestion,
          ingestionRuns: results[index] || null,
        }));

        setIngestionsWithRuns(combinedData);
        queryClient.setQueryData(
          [QUERY_KEYS.ingestions, id, currentPage, pageSize],
          combinedData
        );
      };

      fetchIngestionWithRuns();
    }
  }, [ingestionsResult, ingestionSuccess, id, currentPage, pageSize, queryClient]);

  return { data: ingestionsWithRuns, isLoadingIngestions: !ingestionSuccess };
};

/* export const useIngestionRuns = (
  collectionId: string | undefined,
  ingestionId: string | undefined
) => {
  return useQuery([QUERY_KEYS.runs, collectionId, ingestionId], getIngestionRuns, {
    enabled: Boolean(collectionId) && Boolean(ingestionId),
  });
}; */

export const useManagedIdentities = () => {
  return useQuery([QUERY_KEYS.credentials], getManagedIdentities);
};

export const useCredentialTypes = () => {
  return useQuery([QUERY_KEYS.credentialTypes], getCredentialTypes);
};

export const useCollections = () => {
  return useQuery<{ collections: IStacCollection[] }, AxiosError>(
    [QUERY_KEYS.collections],
    getCollections
  );
};

export const useCollectionItems = (
  collectionId: string,
  limit?: number,
  itemQueryEnabled: boolean = true
) => {
  return useInfiniteQuery(
    [QUERY_KEYS.items, collectionId, limit],
    getCollectionItems,
    {
      enabled: Boolean(collectionId) && itemQueryEnabled,
      getNextPageParam: lastPage => {
        const nextHref = lastPage.links.find(link => link.rel === "next")?.href;

        if (!nextHref) {
          return undefined;
        }

        const nextHrefUrl = new URL(nextHref);
        return nextHrefUrl.searchParams.get("token");
      },
    }
  );
};

export const useStaticMetadata = (staticFileName: string) => {
  return useQuery([QUERY_KEYS.staticFiles, staticFileName], getStaticMetadata);
};

export const useApiPathFragment = (fragment: string = "") => {
  return useQuery([QUERY_KEYS.apiPathFragment, fragment], getApiFragment, {
    enabled: Boolean(fragment),
  });
};

export const useTileJson = (
  query: IMosaic,
  renderOption: IMosaicRenderOption | null,
  collection: IStacCollection | null,
  item: IStacItem | null
) => {
  return useQuery(
    [QUERY_KEYS.tileJson, query, renderOption, collection, item],
    getTileJson,
    {
      retry: 2,
      enabled: Boolean(collection) && Boolean(query.searchId),
    }
  );
};

export const useAnimationExport = (
  config: AnimationConfig,
  collectionId?: string
) => {
  return useQuery(
    [QUERY_KEYS.animationExport, config, collectionId],
    getAnimationExport,
    {
      enabled: false,
    }
  );
};

export const useImageExport = (config: ImageConfig, collectionId?: string) => {
  return useQuery([QUERY_KEYS.imageExport, config, collectionId], getImageExport, {
    enabled: false,
  });
};

export const useAuthImageBlob = (url: string) => {
  const isTokenRequired =
    Boolean(url) && (isAuthRequest(url) || isGraphRequest(url));
  return useQuery([QUERY_KEYS.imageBlob, url], getImageBlob, {
    enabled: isTokenRequired,
  });
};

export const useStacCollectionSchema = () => {
  return useQuery([QUERY_KEYS.stacCollectionSchema], getStacCollectionSchema);
};

export const useMosaics = (collectionId: string) => {
  return useQuery([QUERY_KEYS.mosaics, collectionId], getMosaics);
};

export const useRenderOptions = (id: string) => {
  return useQuery([QUERY_KEYS.renderOptions, id], getRenderOptions);
};

export const useConfigQueryables = (id: string) => {
  return useQuery([QUERY_KEYS.queryables, id], getConfigQueryables);
};

export const useTileSettings = (id?: string) => {
  return useQuery([QUERY_KEYS.tileSettings, id], getTileSettings, {
    enabled: Boolean(id),
  });
};

export const useUpdateTileSettings = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation(updateTileSettings, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.tileSettings, id]);
      queryClient.invalidateQueries([QUERY_KEYS.configManifest, id]);
    },
  });
};

export const downloadFromObjUrl = (objectUrl: string, filename: string) => {
  const element = document.createElement("a");
  element.href = objectUrl;
  element.download = filename;
  document.body.appendChild(element);
  element.click();
};

const getStacCollectionSchema = async (): Promise<JSONSchema> => {
  const schema = await $RefParser.dereference(STAC_COLLECTION_SCHEMA_URI);
  return schema;
};

const getImageBlob = async (
  queryContext: QueryFunctionContext<[string, string]>
): Promise<string> => {
  const [, url] = queryContext.queryKey;

  let data: AxiosRequestConfig = {
    responseType: "blob",
  };

  if (!url.includes("api-version")) {
    Object.assign(data, { params: { "api-version": API_VERSION } });
  }

  const resp = await axios.get(url, data);
  return URL.createObjectURL(resp.data);
};

const getAnimationExport = async (
  queryContext: QueryFunctionContext<[string, AnimationConfig, string?]>
): Promise<ImageExportResponse> => {
  return getExport<AnimationConfig | undefined>("animation", queryContext);
};

const getImageExport = async (
  queryContext: QueryFunctionContext<[string, ImageConfig, string?]>
): Promise<ImageExportResponse> => {
  return getExport<ImageConfig>("static", queryContext);
};

const getExport = async <T>(
  urlEndpoint: string,
  queryContext: QueryFunctionContext<[string, T, string?]>
): Promise<ImageExportResponse> => {
  const [, config, collectionId] = queryContext.queryKey;
  const resp = await axios.post(
    `${IMAGE_URL}/collections/${collectionId}/image/${urlEndpoint}`,
    config,
    {
      params: {
        "api-version": API_VERSION,
      },
    }
  );

  // For local development, swap out azurite host with localhost
  const { url } = resp.data;
  const modifiedUrl = url.startsWith("http://azurite")
    ? url.replace("http://azurite", "http://localhost")
    : url;
  return { url: modifiedUrl };
};

export const getTileJson = async (
  queryContext: QueryFunctionContext<
    [
      string,
      IMosaic,
      IMosaicRenderOption | null,
      IStacCollection | null,
      IStacItem | null
    ]
  >
) => {
  const [, ...args] = queryContext.queryKey;
  return fetchTileJson(...args);
};

export const fetchTileJson = async (
  query: IMosaic,
  renderOption: IMosaicRenderOption | null,
  collection: IStacCollection | null,
  item: IStacItem | null = null
): Promise<ITileJSON> => {
  let url;
  if (renderOption?.type === LayerType.tile) {
    url = makeRasterTileJsonUrl(query, renderOption, collection, item);
  } else if (collection && renderOption) {
    url = getTileJsonAsset(collection, renderOption);
  } else {
    throw new Error("Vector layer requires a collection and render option");
  }

  const resp = await axios.get(url);
  return resp.data;
};

let registerCancelToken: Canceler | null = null;
export const registerStacFilter = async (
  collectionId: string | undefined,
  queryInfo: IMosaic,
  cql: ICqlExpressionList,
  shouldCancelPrevious: boolean = true
): Promise<string> => {
  if (!collectionId) return "";
  // If there is a register request in-flight, cancel it. This is important because
  // this function is called as a result of an async thunk. If two register requests
  // are made very quickly, the first request may return after the second, causing the
  // map layer to reference the wrong mosaic hash tiles
  shouldCancelPrevious && registerCancelToken && registerCancelToken();

  const body = makeFilterBody([collectionFilter(collectionId)], queryInfo, cql);

  const r = await axios.post(`${DATA_URL}/mosaic/register`, body, {
    cancelToken: new axios.CancelToken(c => (registerCancelToken = c)),
    params: {
      "api-version": API_VERSION,
    },
  });
  return r.data.searchid;
};

const getCollection = async (
  queryContext: QueryFunctionContext<[string, string | undefined]>
): Promise<IStacCollection> => {
  const [, id] = queryContext.queryKey;
  const resp = await axios.get(`${STAC_URL}/collections/${id}`, {
    params: { "api-version": API_VERSION },
  });

  return resp.data;
};

const getCollections = async (): Promise<{
  collections: IStacCollection[];
}> => {
  const resp = await axios.get(`${STAC_URL}/collections`, {
    params: { "api-version": API_VERSION },
  });

  return { ...resp.data, collections: resp.data.collections };
};

const getCollectionItems = async (
  queryContext: QueryFunctionContext<[string, string?, number?]>
): Promise<IStacSearchResult> => {
  const [, id, limit] = queryContext.queryKey;
  const { pageParam } = queryContext;

  const pageLimit = limit || 50;
  const pageToken = pageParam ? `&token=${pageParam}` : "";
  const resp = await axios.get(
    `${STAC_URL}/collections/${id}/items?${pageToken}&limit=${pageLimit}&api-version=${API_VERSION}`
  );

  return resp.data;
};

const getCredentials = async (
  queryContext: QueryFunctionContext<[string, number, number]>
): Promise<ICredentialInfo[]> => {
  console.log(queryContext.queryKey);
  const [, currentPage, pageSize] = queryContext.queryKey;
  const resp = await axios.get(`${INMA_URL}/ingestion-sources`, {
    params: {
      "api-version": API_VERSION,
      $top: pageSize,
      $skip: currentPage * pageSize,
    },
  });
  return resp.data.value;
};

const getCredentialDetail = async (
  queryContext: QueryFunctionContext<[string, string | undefined]>
): Promise<IConnectionInfo> => {
  if (!queryContext) return {} as IConnectionInfo;
  const [, credentialId] = queryContext.queryKey;
  const resp = await axios.get(`${INMA_URL}/ingestion-sources/${credentialId}`, {
    params: { "api-version": API_VERSION },
  });
  return resp.data.connectionInfo;
};

const getIngestions = async (
  queryContext: QueryFunctionContext<[string, string | undefined, number, number]>
): Promise<IIngestionDefinitions> => {
  const [, collectionId, currentPage, pageSize] = queryContext.queryKey;
  const resp = await axios.get(
    `${INMA_URL}/collections/${collectionId}/ingestions`,
    {
      params: {
        "api-version": API_VERSION,
        $top: pageSize,
        $skip: currentPage * pageSize,
      },
    }
  );
  return resp.data;
};

const getIngestionRuns = async (
  queryContext: QueryFunctionContext<
    [string, string | undefined, string | undefined]
  >
): Promise<IIngestionRuns> => {
  if (!queryContext) return { value: [] };
  const [, collectionId, ingestionId] = queryContext.queryKey;
  const resp = await axios.get(
    `${INMA_URL}/collections/${collectionId}/ingestions/${ingestionId}/runs`,
    { params: { "api-version": API_VERSION } }
  );
  return resp.data;
};

const getManagedIdentities = async (): Promise<IManagedIdentity[]> => {
  const resp = await axios.get(`${INMA_URL}/ingestion-sources/managed-identities`, {
    params: { "api-version": API_VERSION },
  });
  if (resp.data.value && Array.isArray(resp.data.value)) {
    return resp.data.value;
  }
  return [];
};

const getCredentialTypes = async (): Promise<ICredentialType[]> => {
  return [
    { name: "SasToken", title: "SAS Token" },
    { name: "BlobManagedIdentity", title: "Managed Identity" },
  ];
};

const getMosaics = async (
  queryContext: QueryFunctionContext<[string, string | undefined]>
): Promise<[IMosaic]> => {
  const [, collectionId] = queryContext.queryKey;
  const resp = await axios.get(
    `${STAC_URL}/collections/${collectionId}/configurations/mosaics`,
    {
      params: { "api-version": API_VERSION },
    }
  );

  return resp.data;
};

const getRenderOptions = async (
  queryContext: QueryFunctionContext<[string, string | undefined]>
): Promise<IMosaicRenderOption[]> => {
  const [, id] = queryContext.queryKey;
  const resp = await axios.get(
    `${STAC_URL}/collections/${id}/configurations/render-options`,
    { params: { "api-version": API_VERSION } }
  );

  return resp.data;
};

const getTileSettings = async (
  queryContext: QueryFunctionContext<[string, string | undefined]>
): Promise<ITileSettings> => {
  const [, id] = queryContext.queryKey;
  const resp = await axios.get(
    `${STAC_URL}/collections/${id}/configurations/tile-settings`,
    { params: { "api-version": API_VERSION } }
  );

  return resp.data;
};

// These queryables are used for configuration editing, not query building.
const getConfigQueryables = async (
  queryContext: QueryFunctionContext<[string, string | undefined]>
): Promise<IQueryableDefinition[]> => {
  const [, id] = queryContext.queryKey;
  const resp = await axios.get(`${STAC_URL}/collections/${id}/queryables`, {
    params: { "api-version": API_VERSION },
  });

  const arr: IQueryableDefinition[] = Object.keys(resp.data.properties)
    .filter(key => !defaultQueryableNames.includes(key))
    .map(key => {
      return {
        name: key,
        definition: resp.data.properties[key],
      };
    });
  return arr;
};

const getStaticMetadata = async (
  queryParam: QueryFunctionContext<[string, string]>
) => {
  const [, file] = queryParam.queryKey;
  const resp = await axios.get(`static/metadata/${file}`);
  return resp.data;
};

const getApiFragment = async (
  queryParam: QueryFunctionContext<[string, string]>
): Promise<Record<any, any>> => {
  const [, fragment] = queryParam.queryKey;
  const path = fragment.startsWith("/") ? fragment.slice(1) : fragment;
  const fetchPath = fragment.startsWith("stac/") ? fragment.slice(5) : path;
  const resp = await axios.get<Record<any, any>>(`${STAC_URL}/${fetchPath}`, {
    params: { "api-version": API_VERSION },
  });
  return resp.data;
};
